import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MessagesSquare,
  Users,
  Zap,
  Clock,
  Workflow,
  Layers,
  SlidersHorizontal,
} from 'lucide-react';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';
import { ACCENTS } from './DemoSiteLanding';
import {
  T,
  SURFACE,
  HAIRLINE,
  SHADOW,
  Hero,
  PrimaryButton,
  GhostButton,
  Band,
  CenterHead,
  IconTile,
  StatementBand,
} from './siteUI';

/* =========================================================
   Company page.

   Who FreshRoad is and what it believes — the one page on the site that
   argues for the company rather than a product. Product detail stays on the
   product pages; What We Build here is only a map into them.

   The three products carry the same colours as the landing page (ACCENTS is
   imported rather than restated, so a product and its hue can never drift
   apart between the two pages).
   ========================================================= */

/* Category first, then the product — the reader is being shown the shape of
   the platform, not being sold one product. */
const BUILD = [
  {
    category: 'Customer Operations',
    name: 'Conversational AI',
    accent: 'violet',
    icon: MessagesSquare,
    to: '/site/conversational-ai',
    desc: 'AI agents that serve customers across voice, SMS, chat, and email—24/7.',
  },
  {
    category: 'AI Workforce',
    name: 'Teammate',
    accent: 'emerald',
    icon: Users,
    to: '/site/teammate',
    desc: 'Specialized AI teammates that research, create, analyze, coordinate, and complete work.',
  },
  {
    category: 'AI Management',
    name: 'Orbiter',
    accent: 'orange',
    icon: Zap,
    to: '/site/orbiter',
    desc: "Access, manage, and optimize the world's leading AI models from one place.",
  },
];

const WHY = [
  { name: 'Serve more customers', icon: Clock, desc: 'Be available across every conversation, at any hour.' },
  { name: 'Complete work faster', icon: Workflow, desc: 'Turn repeatable processes into reliable AI-powered workflows.' },
  { name: 'Scale with less complexity', icon: Layers, desc: 'Grow capabilities without adding a maze of disconnected software.' },
  { name: 'Stay in control', icon: SlidersHorizontal, desc: 'Define the knowledge, rules, permissions, and approvals.' },
];

/* Beliefs are full sentences, so they get no icons — a tile beside a sentence
   reads as a feature list, which is the opposite of what this section is. */
const BELIEFS = [
  {
    name: 'AI should do real work.',
    desc: 'It should move beyond answering questions to completing meaningful work and delivering measurable outcomes.',
  },
  {
    name: 'People should stay in control.',
    desc: 'Businesses set the knowledge, rules, permissions, and approvals. AI operates within those boundaries.',
  },
  {
    name: 'The best AI will keep changing.',
    desc: 'FreshRoad is model-independent, so businesses can use the right intelligence for every job.',
  },
  {
    name: 'Powerful technology should be accessible.',
    desc: "Small and growing businesses deserve the same operational capabilities as the world's largest organizations.",
  },
];

/* HashRouter makes href="#beliefs" fight the router, so scroll the element. */
const jumpToBeliefs = () => {
  const el = document.getElementById('what-we-believe');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const DemoSiteCompany = () => (
  <>
    <Breadcrumb trail={['Home', 'Company']} />

    {/* Hero */}
    <section className="grid grid-cols-1 items-center gap-10 py-8 lg:grid-cols-[1.05fr_1fr]">
      <Hero
        eyebrow="Our Company"
        title="We're building the AI operating system for business."
        actions={
          <>
            <PrimaryButton>
              <Link to="/site" className="inline-flex items-center gap-2">
                Explore our platform <ArrowRight size={16} />
              </Link>
            </PrimaryButton>
            <GhostButton onClick={jumpToBeliefs} className="!text-teal-700">
              Our vision
            </GhostButton>
          </>
        }
      >
        FreshRoad helps businesses run and grow with AI—serving customers, completing work, and
        managing the intelligence behind it all.
      </Hero>

      {/* The platform, drawn as its three layers in their product colours. */}
      <div className={`rounded-[28px] bg-slate-50/70 p-8 ${HAIRLINE}`}>
        <div className="flex flex-col gap-3">
          {BUILD.map(({ category, name, icon: Icon, accent }) => {
            const a = ACCENTS[accent];
            return (
              <div
                key={name}
                className={`flex items-center gap-4 rounded-2xl px-5 py-4 ring-1 ${a.card} ${SHADOW.card}`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${a.tile}`}>
                  <Icon size={20} strokeWidth={1.85} />
                </span>
                <div className="min-w-0">
                  <p className={`${T.micro} ${a.chip}`}>{category}</p>
                  <p className="mt-0.5 text-[15px] font-semibold">{name}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Our goal */}
    <StatementBand sub="AI can transform how a business operates—but only when it becomes part of the real work. FreshRoad makes that power practical, affordable, and accessible.">
      Help every business have the capabilities of a{' '}
      <span className="text-teal-600">1,000-person company.</span>
    </StatementBand>

    {/* What we build */}
    <section className="px-2 py-8">
      <CenterHead title="One AI platform, built to run your business." />
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {BUILD.map(({ category, name, desc, icon: Icon, to, accent }) => {
          const a = ACCENTS[accent];
          return (
            <div
              key={name}
              className={`flex flex-col rounded-2xl p-6 ring-1 ${a.card} ${SHADOW.card} transition-shadow duration-200 hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]`}
            >
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${a.tile}`}>
                <Icon size={22} strokeWidth={1.85} />
              </span>
              <p className={`mt-4 ${T.micro} ${a.chip}`}>{category}</p>
              <p className={`mt-1 ${T.h3}`}>{name}</p>
              <p className={`mt-2.5 flex-1 ${T.body}`}>{desc}</p>
              <Link
                to={to}
                className={`mt-6 inline-flex w-fit items-center gap-1.5 rounded-lg text-[13.5px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${a.link}`}
              >
                Learn more <ArrowRight size={15} />
              </Link>
            </div>
          );
        })}
      </div>
    </section>

    {/* Why FreshRoad */}
    <Band title="Built for real businesses.">
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map(({ name, desc, icon: Icon }) => (
          <div key={name} className="flex flex-col">
            <IconTile icon={Icon} />
            <p className="mt-3.5 text-[14.5px] font-semibold">{name}</p>
            <p className={`mt-1.5 ${T.body}`}>{desc}</p>
          </div>
        ))}
      </div>
    </Band>

    {/* What we believe */}
    <section id="what-we-believe" className="scroll-mt-24 px-2 py-8">
      <CenterHead title="AI should make business simpler." />
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {BELIEFS.map(({ name, desc }) => (
          <div key={name} className={`flex flex-col p-6 ${SURFACE.card}`}>
            <p className="text-[15.5px] font-bold leading-snug tracking-[-0.01em]">{name}</p>
            <p className={`mt-2.5 ${T.body}`}>{desc}</p>
          </div>
        ))}
      </div>
    </section>

    <CtaBand
      title="Talk to FreshRoad."
      subtitle="Tell us what your business runs on, and we'll show you what it looks like with AI doing the work."
      primary="Book a demo"
    />
  </>
);

export default DemoSiteCompany;
